import { useEffect, useMemo, useState } from 'react';
import type { DayRestriction, ScheduleConfig, Tenant, Zone } from '@irrigation/core';
import { resolveWindowEnd, windowMinutes } from '@irrigation/core';
import { api } from '../api.js';
import { dailyDemandMinutes } from '../lib/estimate.js';
import { windowError } from '../lib/schedule.js';
import { searchPlaces, type GeoPlace } from '../lib/geocode.js';
import {
  Banner,
  Button,
  Card,
  CardSkeleton,
  Field,
  Select,
  Switch,
  TextInput,
  useToast,
} from '../components/ui.js';
import { errorMessage, fmtMinutes, fmtTime, WEEKDAY_LABELS } from '../lib/format.js';
import { useLoad } from '../lib/useLoad.js';

async function loadSettings(): Promise<{ tenant: Tenant; config: ScheduleConfig; zones: Zone[] }> {
  const [tenant, config, zones] = await Promise.all([api.getMe(), api.getConfig(), api.listZones()]);
  return { tenant, config, zones };
}

export function Settings() {
  const { data, loading, error } = useLoad(loadSettings);

  if (loading) {
    return (
      <div className="stack">
        <CardSkeleton lines={2} />
        <CardSkeleton lines={3} />
      </div>
    );
  }
  if (error || !data) return <Banner tone="error">{error || 'Failed to load.'}</Banner>;

  return <SettingsForm tenant={data.tenant} config={data.config} zones={data.zones} />;
}

/* ---------- form state ---------- */

type DayKind = DayRestriction['kind'];

const DAY_KINDS: readonly DayKind[] = ['ANY', 'ODD_DAYS', 'EVEN_DAYS', 'SPECIFIC_DAYS', 'INTERVAL'];

const DAY_KIND_LABELS: Record<DayKind, string> = {
  ANY: 'Any day (recommended)',
  ODD_DAYS: 'Odd calendar days',
  EVEN_DAYS: 'Even calendar days',
  SPECIFIC_DAYS: 'Specific weekdays',
  INTERVAL: 'Every N days',
};

interface NumSpec {
  min: number;
  max: number;
  msg: string;
}

const NUM_SPECS = {
  latitude: { min: -90, max: 90, msg: 'Latitude must be between −90 and 90.' },
  longitude: { min: -180, max: 180, msg: 'Longitude must be between −180 and 180.' },
  freezeTempF: { min: -20, max: 60, msg: 'Use a temperature between −20 and 60 °F.' },
  qualifyingRainIn: { min: 0, max: 3, msg: 'Use a depth between 0 and 3 inches.' },
  rainLookbackDays: { min: 0, max: 7, msg: 'Use 0–7 days.' },
  rainAheadDays: { min: 0, max: 3, msg: 'Use 0–3 days.' },
  minRainProbabilityPct: { min: 0, max: 100, msg: 'Use 0–100%.' },
  windSkipMph: { min: 1, max: 60, msg: 'Use 1–60 mph.' },
  soakMinutes: { min: 0, max: 180, msg: 'Use 0–180 minutes.' },
  maxZoneRuntimeMinutes: { min: 5, max: 360, msg: 'Use 5–360 minutes.' },
  maxDailyWaterIn: { min: 0.1, max: 3, msg: 'Use 0.1–3 inches.' },
  minSensorRisePct: { min: 0, max: 50, msg: 'Use 0–50%.' },
  everyNDays: { min: 1, max: 14, msg: 'Use 1–14 days.' },
} satisfies Record<string, NumSpec>;

type NumField = keyof typeof NUM_SPECS;

function SettingsForm({ tenant, config, zones }: { tenant: Tenant; config: ScheduleConfig; zones: Zone[] }) {
  const toast = useToast();

  // Numeric fields are edited as strings so partial input never coerces to 0.
  const [nums, setNums] = useState<Record<NumField, string>>({
    latitude: tenant.location ? String(tenant.location.latitude) : '',
    longitude: tenant.location ? String(tenant.location.longitude) : '',
    freezeTempF: String(config.skips.freezeTempF),
    qualifyingRainIn: String(config.skips.qualifyingRainIn),
    rainLookbackDays: String(config.skips.rainLookbackDays),
    rainAheadDays: String(config.skips.rainAheadDays),
    minRainProbabilityPct: String(config.skips.minRainProbabilityPct ?? 50),
    windSkipMph: String(config.skips.windSkipMph),
    soakMinutes: String(config.soakMinutes),
    maxZoneRuntimeMinutes: String(config.maxZoneRuntimeMinutes),
    maxDailyWaterIn: String(config.maxDailyWaterIn),
    minSensorRisePct: String(config.minSensorRisePct ?? 2),
    everyNDays: String(config.dayRestriction.kind === 'INTERVAL' ? config.dayRestriction.everyNDays : 3),
  });
  const [cycleSoakEnabled, setCycleSoakEnabled] = useState(config.cycleSoakEnabled !== false);
  const [emailSummaries, setEmailSummaries] = useState(config.emailSummaries);
  const [winterized, setWinterized] = useState(config.winterized === true);
  const [timezone, setTimezone] = useState(tenant.location?.timezone ?? detectTimezone() ?? '');
  const [earliestStart, setEarliestStart] = useState(config.window.earliestStart);
  const [latestEnd, setLatestEnd] = useState(config.window.latestEnd);
  const [finishByEnd, setFinishByEnd] = useState(config.window.finishByEnd === true);
  const [endBySunrise, setEndBySunrise] = useState(config.window.endMode === 'SUNRISE');
  // Offset stored signed (negative = before sunrise); the UI edits magnitude + direction.
  const [sunriseOffsetMinutes, setSunriseOffsetMinutes] = useState(config.window.sunriseOffsetMinutes ?? 0);
  // Today's local sunrise for the preview; { time: undefined } once fetched but unavailable.
  const [sunrise, setSunrise] = useState<{ loaded: boolean; time?: string }>({ loaded: false });
  useEffect(() => {
    let alive = true;
    api
      .getSunrise()
      .then((r) => alive && setSunrise({ loaded: true, time: r.sunriseLocal }))
      .catch(() => alive && setSunrise({ loaded: true }));
    return () => {
      alive = false;
    };
  }, []);
  const [dayKind, setDayKind] = useState<DayKind>(config.dayRestriction.kind);
  const [weekdays, setWeekdays] = useState<Set<number>>(
    new Set(config.dayRestriction.kind === 'SPECIFIC_DAYS' ? config.dayRestriction.weekdays : []),
  );
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const [locating, setLocating] = useState(false);

  // Place search (Open-Meteo geocoding).
  const [placeQuery, setPlaceQuery] = useState('');
  const [places, setPlaces] = useState<GeoPlace[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchNote, setSearchNote] = useState('');

  const timezones = useMemo(supportedTimezones, []);

  const setNum = (key: NumField, value: string) => {
    setNums((d) => ({ ...d, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
    setDirty(true);
  };

  const touch = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v);
    setDirty(true);
  };

  const doSearch = async () => {
    if (!placeQuery.trim()) return;
    setSearching(true);
    setSearchNote('');
    setPlaces([]);
    try {
      const found = await searchPlaces(placeQuery);
      setPlaces(found);
      if (found.length === 0) setSearchNote('No places found — try a nearby city or town name.');
    } catch (e) {
      setSearchNote(errorMessage(e, 'Place search failed.'));
    } finally {
      setSearching(false);
    }
  };

  const applyPlace = (p: GeoPlace) => {
    setNum('latitude', p.latitude.toFixed(4));
    setNum('longitude', p.longitude.toFixed(4));
    if (p.timezone) {
      setTimezone(p.timezone);
      setErrors((er) => ({ ...er, timezone: undefined }));
    }
    setDirty(true);
    setPlaces([]);
    setPlaceQuery('');
    setSearchNote(`Using ${p.name}${p.region ? `, ${p.region}` : ''} — coordinates and timezone filled in below.`);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      setFormError('Geolocation is not available in this browser — enter coordinates manually.');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setNum('latitude', pos.coords.latitude.toFixed(4));
        setNum('longitude', pos.coords.longitude.toFixed(4));
        // Also adopt the device's timezone, the way place search does. Moving
        // the coordinates without it leaves a stale zone — and the window is
        // interpreted in local time, so watering would run at the wrong hour.
        const device = detectTimezone();
        const moved = device && device !== timezone;
        if (device) {
          setTimezone(device);
          setErrors((er) => ({ ...er, timezone: undefined }));
        }
        setSearchNote(
          moved
            ? `Using your current location — coordinates and timezone (${device}) filled in below.`
            : 'Using your current location — coordinates filled in below.',
        );
        setLocating(false);
      },
      () => {
        setFormError('Could not read your location — enter coordinates manually.');
        setLocating(false);
      },
      { timeout: 10000 },
    );
  };

  const save = async () => {
    const parsed = {} as Record<NumField, number>;
    const errs: Partial<Record<string, string>> = {};

    for (const key of Object.keys(NUM_SPECS) as NumField[]) {
      if (key === 'everyNDays' && dayKind !== 'INTERVAL') continue;
      const spec = NUM_SPECS[key];
      const n = Number(nums[key].trim());
      if (nums[key].trim() === '' || !Number.isFinite(n)) errs[key] = 'Enter a number.';
      else if (n < spec.min || n > spec.max) errs[key] = spec.msg;
      else parsed[key] = n;
    }
    if (!timezone.trim() || !isValidTimezone(timezone.trim())) {
      errs.timezone = 'Enter a valid IANA timezone, e.g. America/Denver.';
    }
    // A "finish by" earlier than the start is a window that wraps past midnight
    // (e.g. 22:00 → 06:00); only an identical time is meaningless.
    const windowErr = windowError(earliestStart, latestEnd);
    if (windowErr) errs.latestEnd = windowErr;
    if (dayKind === 'SPECIFIC_DAYS' && weekdays.size === 0) {
      errs.weekdays = 'Pick at least one weekday.';
    }

    setErrors(errs);
    if (Object.keys(errs).some((k) => errs[k])) {
      setFormError('Fix the highlighted fields before saving.');
      return;
    }

    const dayRestriction: DayRestriction =
      dayKind === 'SPECIFIC_DAYS'
        ? { kind: 'SPECIFIC_DAYS', weekdays: [...weekdays].sort() }
        : dayKind === 'INTERVAL'
          ? { kind: 'INTERVAL', everyNDays: parsed.everyNDays }
          : { kind: dayKind };

    setBusy(true);
    setFormError('');
    try {
      await Promise.all([
        api.updateMe({
          location: { latitude: parsed.latitude, longitude: parsed.longitude, timezone: timezone.trim() },
        }),
        api.updateConfig({
          window: {
            earliestStart,
            latestEnd,
            finishByEnd,
            endMode: endBySunrise ? 'SUNRISE' : 'FIXED',
            sunriseOffsetMinutes: endBySunrise ? sunriseOffsetMinutes : 0,
          },
          dayRestriction,
          skips: {
            freezeTempF: parsed.freezeTempF,
            qualifyingRainIn: parsed.qualifyingRainIn,
            rainLookbackDays: parsed.rainLookbackDays,
            rainAheadDays: parsed.rainAheadDays,
            minRainProbabilityPct: parsed.minRainProbabilityPct,
            windSkipMph: parsed.windSkipMph,
          },
          soakMinutes: parsed.soakMinutes,
          cycleSoakEnabled,
          maxZoneRuntimeMinutes: parsed.maxZoneRuntimeMinutes,
          maxDailyWaterIn: parsed.maxDailyWaterIn,
          minSensorRisePct: parsed.minSensorRisePct,
          emailSummaries,
          winterized,
        }),
      ]);
      setDirty(false);
      toast('Settings saved.');
    } catch (e) {
      setFormError(errorMessage(e, 'Failed to save.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>Settings</h1>
          <div className="sub">Location, watering window, and the rules that skip a run.</div>
        </div>
        <Button busy={busy} disabled={!dirty} onClick={() => void save()}>
          {dirty ? 'Save changes' : 'Saved'}
        </Button>
      </div>

      {formError && <Banner tone="error">{formError}</Banner>}

      <Card title="Location" sub="Weather and reference evapotranspiration are fetched for these coordinates.">
        <div className="stack">
          <Field
            label="Find your place"
            hint="Search a city or town — coordinates and timezone are filled in automatically. Weather data is regional (~1–10 km), so city-level is as precise as it gets."
          >
            {(id) => (
              <div className="input-wrap">
                <TextInput
                  id={id}
                  value={placeQuery}
                  placeholder="e.g. Littleton, Colorado"
                  onChange={(e) => setPlaceQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void doSearch();
                    }
                  }}
                />
                <button type="button" className="input-affix" disabled={searching} onClick={() => void doSearch()}>
                  {searching ? '…' : 'Search'}
                </button>
              </div>
            )}
          </Field>
          {searchNote && <div className="small muted">{searchNote}</div>}
          {places.length > 0 && (
            <div className="list">
              {places.map((p, i) => (
                <div className="run-row" key={`${p.name}-${p.latitude}-${i}`}>
                  <div className="grow">
                    <div className="title">{p.name}</div>
                    <div className="meta">
                      {[p.region, p.country].filter(Boolean).join(', ')} · {p.latitude.toFixed(2)},{' '}
                      {p.longitude.toFixed(2)}
                    </div>
                  </div>
                  <Button variant="subtle" size="sm" onClick={() => applyPlace(p)}>
                    Use
                  </Button>
                </div>
              ))}
            </div>
          )}
          <div className="grid cols-3">
            <Field label="Latitude" error={errors.latitude}>
              {(id, invalid) => (
                <TextInput
                  id={id}
                  inputMode="decimal"
                  aria-invalid={invalid}
                  value={nums.latitude}
                  onChange={(e) => setNum('latitude', e.target.value)}
                  placeholder="39.7392"
                />
              )}
            </Field>
            <Field label="Longitude" error={errors.longitude}>
              {(id, invalid) => (
                <TextInput
                  id={id}
                  inputMode="decimal"
                  aria-invalid={invalid}
                  value={nums.longitude}
                  onChange={(e) => setNum('longitude', e.target.value)}
                  placeholder="-104.9903"
                />
              )}
            </Field>
            <Field label="Timezone" error={errors.timezone} hint="IANA name, e.g. America/Denver.">
              {(id, invalid) => (
                <>
                  <TextInput
                    id={id}
                    aria-invalid={invalid}
                    list="tz-list"
                    value={timezone}
                    onChange={(e) => {
                      setTimezone(e.target.value);
                      setErrors((er) => ({ ...er, timezone: undefined }));
                      setDirty(true);
                    }}
                  />
                  {timezones.length > 0 && (
                    <datalist id="tz-list">
                      {timezones.map((tz) => (
                        <option key={tz} value={tz} />
                      ))}
                    </datalist>
                  )}
                </>
              )}
            </Field>
          </div>
          <div className="actions">
            <Button variant="ghost" size="sm" busy={locating} onClick={useMyLocation}>
              Use my location
            </Button>
            {detectTimezone() && timezone !== detectTimezone() && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setTimezone(detectTimezone()!);
                  setDirty(true);
                }}
              >
                Use this device's timezone ({detectTimezone()})
              </Button>
            )}
          </div>
        </div>
      </Card>

      <Card
        title="Watering window"
        sub={
          earliestStart > latestEnd
            ? `Runs start no earlier than ${fmtTime(earliestStart)} and finish by ${fmtTime(latestEnd)} the next morning, local time.`
            : `Runs start no earlier than ${fmtTime(earliestStart)} and must finish by ${fmtTime(latestEnd)}, local time.`
        }
      >
        <WindowCapacityNote zones={zones} config={config} earliestStart={earliestStart} latestEnd={latestEnd} />
        <div className="grid cols-3">
          <Field label="Earliest start" hint="Pre-dawn watering loses the least to evaporation.">
            {(id) => (
              <TextInput id={id} type="time" value={earliestStart} onChange={(e) => touch(setEarliestStart)(e.target.value)} />
            )}
          </Field>
          <Field
            label={endBySunrise ? 'Finish by (fallback)' : 'Finish by'}
            error={errors.latestEnd}
            hint={endBySunrise ? 'Used only when sunrise is unavailable.' : undefined}
          >
            {(id, invalid) => (
              <TextInput
                id={id}
                type="time"
                aria-invalid={invalid}
                value={latestEnd}
                onChange={(e) => {
                  setLatestEnd(e.target.value);
                  setErrors((er) => ({ ...er, latestEnd: undefined }));
                  setDirty(true);
                }}
              />
            )}
          </Field>
          <Field label="Soak between cycles (min)" error={errors.soakMinutes} hint="Pause between cycle-and-soak pulses.">
            {(id, invalid) => (
              <TextInput
                id={id}
                inputMode="numeric"
                aria-invalid={invalid}
                disabled={!cycleSoakEnabled}
                value={nums.soakMinutes}
                onChange={(e) => setNum('soakMinutes', e.target.value)}
              />
            )}
          </Field>
        </div>
        <div className="spread" style={{ marginTop: 12 }}>
          <div>
            <div className="small" style={{ fontWeight: 600 }}>
              End at sunrise
            </div>
            <div className="small muted">
              Close the window relative to each day's sunrise instead of a fixed clock time — so watering tracks the
              seasons automatically. The “Finish by” time above becomes a fallback for days sunrise can't be fetched.
            </div>
          </div>
          <Switch
            checked={endBySunrise}
            onChange={(v) => {
              setEndBySunrise(v);
              setDirty(true);
            }}
            label="End at sunrise"
            hideLabel
          />
        </div>
        {endBySunrise && (
          <div className="grid cols-3" style={{ marginTop: 12, alignItems: 'end' }}>
            <Field label="Minutes" hint="How far from sunrise to close.">
              {(id) => (
                <TextInput
                  id={id}
                  inputMode="numeric"
                  value={String(Math.abs(sunriseOffsetMinutes))}
                  onChange={(e) => {
                    const mag = Math.max(0, Math.min(180, Math.round(Number(e.target.value) || 0)));
                    setSunriseOffsetMinutes(sunriseOffsetMinutes < 0 ? -mag : mag);
                    setDirty(true);
                  }}
                />
              )}
            </Field>
            <Field label="Relative to sunrise">
              {(id) => (
                <Select
                  id={id}
                  value={sunriseOffsetMinutes < 0 ? 'before' : 'after'}
                  options={['before', 'after'] as const}
                  labels={{ before: 'before sunrise', after: 'after sunrise' }}
                  onChange={(v) => {
                    const mag = Math.abs(sunriseOffsetMinutes);
                    setSunriseOffsetMinutes(v === 'before' ? -mag : mag);
                    setDirty(true);
                  }}
                />
              )}
            </Field>
            <div className="small muted" style={{ gridColumn: '1 / -1' }}>
              {!sunrise.loaded
                ? 'Checking today’s sunrise…'
                : sunrise.time
                  ? `Sunrise today is ${fmtTime(sunrise.time)} — tonight’s window would close around ${fmtTime(
                      resolveWindowEnd(
                        { earliestStart, latestEnd, endMode: 'SUNRISE', sunriseOffsetMinutes },
                        sunrise.time,
                      ),
                    )}. It shifts a little each day with the season.`
                  : 'Could not fetch today’s sunrise — check your location above.'}
            </div>
          </div>
        )}
        <div className="spread" style={{ marginTop: 12 }}>
          <div>
            <div className="small" style={{ fontWeight: 600 }}>
              Cycle &amp; soak
            </div>
            <div className="small muted">
              Split a long watering into shorter pulses so it soaks in instead of running off. Turn off to deliver each
              zone in one continuous pass — only on flat, fast-draining ground where runoff isn't a concern.
            </div>
          </div>
          <Switch
            checked={cycleSoakEnabled}
            onChange={(v) => {
              setCycleSoakEnabled(v);
              setDirty(true);
            }}
            label="Cycle & soak"
            hideLabel
          />
        </div>
        <div className="spread" style={{ marginTop: 12 }}>
          <div>
            <div className="small" style={{ fontWeight: 600 }}>
              Finish at the end of the window
            </div>
            <div className="small muted">
              Water as late as possible while still finishing by {fmtTime(latestEnd)}, so the lawn doesn't sit wet
              overnight. Off means watering starts at {fmtTime(earliestStart)}.
            </div>
          </div>
          <Switch
            checked={finishByEnd}
            onChange={(v) => {
              setFinishByEnd(v);
              setDirty(true);
            }}
            label="Finish at the end of the window"
            hideLabel
          />
        </div>
      </Card>

      <Card title="Watering days" sub="Restrict which days watering is allowed — e.g. municipal odd/even rules.">
        <div className="stack">
          <div className="grid cols-2">
            <Field label="Allowed days">
              {(id) => <Select id={id} value={dayKind} options={DAY_KINDS} labels={DAY_KIND_LABELS} onChange={touch(setDayKind)} />}
            </Field>
            {dayKind === 'INTERVAL' && (
              <Field label="Water every (days)" error={errors.everyNDays}>
                {(id, invalid) => (
                  <TextInput
                    id={id}
                    inputMode="numeric"
                    aria-invalid={invalid}
                    value={nums.everyNDays}
                    onChange={(e) => setNum('everyNDays', e.target.value)}
                  />
                )}
              </Field>
            )}
          </div>
          {dayKind === 'SPECIFIC_DAYS' && (
            <div className="field">
              <label>Allowed weekdays</label>
              <div className="chip-row" role="group" aria-label="Allowed weekdays">
                {WEEKDAY_LABELS.map((label, i) => (
                  <button
                    key={label}
                    type="button"
                    className={`chip${weekdays.has(i) ? ' on' : ''}`}
                    aria-pressed={weekdays.has(i)}
                    onClick={() => {
                      setWeekdays((s) => {
                        const next = new Set(s);
                        if (next.has(i)) next.delete(i);
                        else next.add(i);
                        return next;
                      });
                      setErrors((er) => ({ ...er, weekdays: undefined }));
                      setDirty(true);
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {errors.weekdays && <span className="field-error">{errors.weekdays}</span>}
            </div>
          )}
        </div>
      </Card>

      <Card title="Smart skips" sub="A planned run is skipped when the forecast crosses these thresholds.">
        <p className="small muted" style={{ marginBottom: '0.75rem' }}>
          Currently skipping for rain (≥ {nums.qualifyingRainIn || '—'} in in the last{' '}
          {nums.rainLookbackDays || '—'} day(s), ≥ {nums.minRainProbabilityPct || '—'}% confidence), freeze (below{' '}
          {nums.freezeTempF || '—'} °F), and wind (above {nums.windSkipMph || '—'} mph).
        </p>
        <details className="form-section collapsible">
          <summary>
            <h3>Thresholds</h3>
            <span className="section-summary">Defaults suit most yards</span>
          </summary>
        <div className="grid cols-4">
          <Field label="Freeze below (°F)" error={errors.freezeTempF}>
            {(id, invalid) => (
              <TextInput
                id={id}
                inputMode="numeric"
                aria-invalid={invalid}
                value={nums.freezeTempF}
                onChange={(e) => setNum('freezeTempF', e.target.value)}
              />
            )}
          </Field>
          <Field label="Qualifying rain (in)" error={errors.qualifyingRainIn}>
            {(id, invalid) => (
              <TextInput
                id={id}
                inputMode="decimal"
                aria-invalid={invalid}
                value={nums.qualifyingRainIn}
                onChange={(e) => setNum('qualifyingRainIn', e.target.value)}
              />
            )}
          </Field>
          <Field label="Rain lookback (days)" error={errors.rainLookbackDays}>
            {(id, invalid) => (
              <TextInput
                id={id}
                inputMode="numeric"
                aria-invalid={invalid}
                value={nums.rainLookbackDays}
                onChange={(e) => setNum('rainLookbackDays', e.target.value)}
              />
            )}
          </Field>
          <Field
            label="Defer for rain within (days)"
            error={errors.rainAheadDays}
            hint="Marginally-due zones wait for forecast rain; 0 disables."
          >
            {(id, invalid) => (
              <TextInput
                id={id}
                inputMode="numeric"
                aria-invalid={invalid}
                value={nums.rainAheadDays}
                onChange={(e) => setNum('rainAheadDays', e.target.value)}
              />
            )}
          </Field>
          <Field
            label="Min rain confidence (%)"
            error={errors.minRainProbabilityPct}
            hint="Ignore forecast rain below this chance when deciding to skip; 0 disables."
          >
            {(id, invalid) => (
              <TextInput
                id={id}
                inputMode="numeric"
                aria-invalid={invalid}
                value={nums.minRainProbabilityPct}
                onChange={(e) => setNum('minRainProbabilityPct', e.target.value)}
              />
            )}
          </Field>
          <Field label="Wind above (mph)" error={errors.windSkipMph}>
            {(id, invalid) => (
              <TextInput
                id={id}
                inputMode="numeric"
                aria-invalid={invalid}
                value={nums.windSkipMph}
                onChange={(e) => setNum('windSkipMph', e.target.value)}
              />
            )}
          </Field>
        </div>
        </details>
      </Card>

      <Card title="Safety caps" sub="Hard caps the scheduler enforces no matter what the planner computes.">
        <div className="stack">
          <div className="grid cols-2">
            <Field
              label="Max zone runtime (min/day)"
              error={errors.maxZoneRuntimeMinutes}
              hint="A single zone never runs longer than this in one day."
            >
              {(id, invalid) => (
                <TextInput
                  id={id}
                  inputMode="numeric"
                  aria-invalid={invalid}
                  value={nums.maxZoneRuntimeMinutes}
                  onChange={(e) => setNum('maxZoneRuntimeMinutes', e.target.value)}
                />
              )}
            </Field>
            <Field
              label="Max water per zone (in/day)"
              error={errors.maxDailyWaterIn}
              hint="A single zone never receives more than this in one day."
            >
              {(id, invalid) => (
                <TextInput
                  id={id}
                  inputMode="decimal"
                  aria-invalid={invalid}
                  value={nums.maxDailyWaterIn}
                  onChange={(e) => setNum('maxDailyWaterIn', e.target.value)}
                />
              )}
            </Field>
          </div>
          <Field
            label="Sensor-response alert (min rise %)"
            error={errors.minSensorRisePct}
            hint="Alert when a watered zone's own sensor rises less than this; catches water that isn't reaching the zone. 0 disables. Runs too brief to move a probe aren't judged. Applies only to a sensor dedicated to one zone."
          >
            {(id, invalid) => (
              <TextInput
                id={id}
                inputMode="numeric"
                aria-invalid={invalid}
                value={nums.minSensorRisePct}
                onChange={(e) => setNum('minSensorRisePct', e.target.value)}
              />
            )}
          </Field>
        </div>
      </Card>

      <Card title="Notifications">
        {config.emailDisabledReason && !emailSummaries && (
          <Banner tone="error">
            {config.emailDisabledReason === 'BOUNCE' ? (
              <>
                Plan email was turned off because messages to {tenant.email} couldn't be delivered.
                Check the address is correct, then switch it back on.
              </>
            ) : (
              <>
                Plan email was turned off because a message to {tenant.email} was reported as spam.
                Switching it back on will resume sending.
              </>
            )}
          </Banner>
        )}
        <div className="spread">
          <div>
            <div className="small" style={{ fontWeight: 600 }}>
              Plan email
            </div>
            <div className="small muted">
              An 8pm summary of what will water, what's skipped, and why — plus an update just before
              watering if the forecast changed the plan. Sent to {tenant.email}. Quiet on days nothing
              waters, unless a sensor needs attention.
            </div>
          </div>
          <Switch
            checked={emailSummaries}
            onChange={(v) => {
              setEmailSummaries(v);
              setDirty(true);
            }}
            label="Plan email"
            hideLabel
          />
        </div>
      </Card>

      <Card
        title="Seasonal shutdown"
        sub="For winter: planning, watering, and emails stop entirely until you resume."
      >
        <div className="stack">
          <div className="spread">
            <div>
              <div className="small" style={{ fontWeight: 600 }}>
                Winterized
              </div>
              <div className="small muted">
                Flip this on after blowing out the lines in fall. Unlike pause, nothing is planned or recorded all
                winter.
              </div>
            </div>
            <Switch
              checked={winterized}
              onChange={(v) => {
                setWinterized(v);
                setDirty(true);
              }}
              label="Winterized"
              hideLabel
            />
          </div>
        </div>
      </Card>

      {/* Sticky while there is something to save: this form is ~5 screens tall
          on a phone, so editing in the middle left both save buttons offscreen
          and an abandoned tab lost the edits silently. */}
      <div className={`actions save-bar${dirty ? ' dirty' : ''}`}>
        <Button busy={busy} disabled={!dirty} onClick={() => void save()}>
          {dirty ? 'Save changes' : 'Saved'}
        </Button>
        {dirty && <span className="muted small">Unsaved changes</span>}
      </div>
    </div>
  );
}

/**
 * Warn when the zones' worst-case daily demand can't fit in the window —
 * the planner will defer the overflow to the next day.
 */
function WindowCapacityNote({
  zones,
  config,
  earliestStart,
  latestEnd,
}: {
  zones: Zone[];
  config: ScheduleConfig;
  earliestStart: string;
  latestEnd: string;
}) {
  const demand = dailyDemandMinutes(zones, config);
  const capacity = windowMinutes({ earliestStart, latestEnd });
  if (demand <= capacity || capacity <= 0 || zones.length === 0) return null;
  return (
    <div style={{ marginBottom: '0.9rem' }}>
      <Banner tone="info">
        On a heavy day your zones can need ≈ {fmtMinutes(demand)}, but this window holds {fmtMinutes(capacity)}.
        The neediest zones water first and the rest are deferred to the next day — widen the window if you'd rather
        avoid deferrals.
      </Banner>
    </div>
  );
}

/* ---------- timezone helpers ---------- */

function detectTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

function isValidTimezone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

function supportedTimezones(): string[] {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch {
    return [];
  }
}
